/**
 * Renders a stored history value as something a person can read.
 *
 * The audit trail keeps values exactly as they were written, so one field can
 * arrive as a Date, an ISO string, epoch milliseconds, an enum object or a bare
 * key. The dictionary says what the field *is*; this module says how that looks.
 *
 * Dates are converted ONLY where the dictionary declares a date. Guessing from
 * the value was measured against the real data and would misfire: JMBG
 * (2810987762623), bank account parts (0030042674020) and PIB all look like
 * epoch milliseconds and are not dates. The one genuine epoch field found,
 * `Request.dateOfCreation`, stores "1725148800000" as a *string* — so the date
 * branch accepts numbers and numeric strings alike, but only for date fields.
 */

import dayjs from 'dayjs';
import { DATE_FORMATS } from '@/utils/formUtils';
import { FIELD_TYPE } from './fieldDictionary';

/** Shown wherever a value is absent. */
export const EMPTY_DISPLAY = '—';

const SENSITIVE_HINTS = [
  'password',
  'passwordtoken',
  'token',
  'secret',
  'apikey',
  'privatekey',
];

/**
 * True when a field name suggests a credential, regardless of the dictionary.
 * Belt and braces: a newly added `refreshToken` is masked before anyone
 * remembers to describe it.
 *
 * @param {string} path - Field path.
 * @returns {boolean}
 */
export const isSensitivePath = (path) => {
  const lowered = String(path ?? '').toLowerCase();
  return SENSITIVE_HINTS.some((hint) => lowered.includes(hint));
};

/** Shown in place of any credential that is set. */
const MASKED_DISPLAY = '******';

/**
 * Hides a credential completely; only "set" versus "cleared" stays visible.
 *
 * No part of the value is ever shown. A tail of a password, token or API key is
 * itself a leak, and the server now sends redaction markers (`[REDACTED]`,
 * `__redacted__`) whose tail would read like a fragment of the real value.
 *
 * @param {*} value - Raw value.
 * @returns {string}
 */
const maskValue = (value) => {
  if (value === null || value === undefined || value === '') return EMPTY_DISPLAY;
  return MASKED_DISPLAY;
};

/**
 * True for values that carry nothing, however the side spells it.
 *
 * Mirrors the rule the backend applies when deciding whether a change is real
 * (`history.services.js`), so the drawer never contradicts what was stored.
 *
 * @param {*} value - Value from either side.
 * @returns {boolean}
 */
export const isEmptyHistoryValue = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value instanceof Date) return false;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Parses the shapes a date is stored in: Date, ISO string, epoch milliseconds
 * as a number, or epoch milliseconds as a numeric string.
 *
 * @param {*} value - Raw value from a field the dictionary calls a date.
 * @returns {dayjs.Dayjs|null} A valid dayjs instance, or null.
 */
const parseStoredDate = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  if (typeof value === 'string' && /^\d{10,14}$/.test(value)) {
    const parsed = dayjs(Number(value));
    return parsed.isValid() ? parsed : null;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

/**
 * Picks the human name out of a populated reference.
 *
 * @param {*} value - Reference value: a populated document, an id, or null.
 * @returns {string|null} A name, or null when there is nothing better than the id.
 */
export const getReferenceLabel = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'object') return String(value);

  if (value.name) return String(value.name);
  if (value.sectorName) return String(value.sectorName);
  if (value.title) return String(value.title);
  if (value.label) return String(value.label);
  if (value.firstName || value.lastName) {
    return [value.firstName, value.lastName].filter(Boolean).join(' ');
  }
  if (value.originalName) return String(value.originalName);
  if (value.email) return String(value.email);
  if (value._id) return String(value._id);

  return null;
};

/**
 * Formats a number without inventing precision: whole numbers stay whole.
 *
 * @param {number} value - Numeric value.
 * @param {string} locale - BCP 47 tag.
 * @returns {string}
 */
const formatNumber = (value, locale) => {
  if (!Number.isFinite(value)) return String(value);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Renders one stored value for display.
 *
 * @param {*} value - The raw value as stored.
 * @param {Object} descriptor - Result of `describeField`.
 * @param {Object} options - Rendering context.
 * @param {Function} options.t - i18next `t`, bound to the `history` namespace.
 * @param {string} [options.locale] - BCP 47 tag for number formatting.
 * @returns {string} Always a string; never throws on odd input.
 */
export const formatHistoryValue = (value, descriptor = {}, options = {}) => {
  const { t, locale = 'sr' } = options;
  const { type = FIELD_TYPE.text, enumSet, path } = descriptor;
  const translate =
    typeof t === 'function' ? t : (_key, opts) => opts?.defaultValue ?? _key;

  if (type === FIELD_TYPE.sensitive || isSensitivePath(path)) {
    return maskValue(value);
  }

  if (isEmptyHistoryValue(value)) return EMPTY_DISPLAY;

  // Checked before the type switch: these arrive on fields the dictionary
  // types as plain text, so no branch below would ever look at them.
  const method = formatCalculationMethod(value, translate);
  if (method) return method;

  switch (type) {
    case FIELD_TYPE.bool:
      return value
        ? translate('value.yes', { defaultValue: 'Da' })
        : translate('value.no', { defaultValue: 'Ne' });

    case FIELD_TYPE.date: {
      const parsed = parseStoredDate(value);
      return parsed ? parsed.format(DATE_FORMATS.DISPLAY_DOTS) : String(value);
    }

    case FIELD_TYPE.datetime: {
      const parsed = parseStoredDate(value);
      return parsed
        ? `${parsed.format(DATE_FORMATS.DISPLAY_DOTS)} ${parsed.format(
            DATE_FORMATS.TIME_ONLY
          )}`
        : String(value);
    }

    case FIELD_TYPE.enum: {
      const isObject = typeof value === 'object';
      const key = isObject ? (value.key ?? value.value) : value;

      // Written as an explicit branch rather than `&& … ??`. That form yields
      // the boolean `false` for anything that is not an object, and `??` lets
      // `false` through as a real value instead of falling back — so every enum
      // key missing from the translations rendered as the word "false".
      const fallbackSource = isObject ? (value.label ?? value.value ?? key) : value;
      const fallback = String(fallbackSource ?? '');

      if (!enumSet || key === undefined || key === null) return fallback;

      return translate(`enum.${enumSet}.${key}`, { defaultValue: fallback });
    }

    case FIELD_TYPE.ref: {
      const label = getReferenceLabel(value);
      return label ?? EMPTY_DISPLAY;
    }

    // Days and hours render as bare numbers: the field label already says what
    // is being counted ("Dana godišnjeg odmora: 20 → 10"), so a unit suffix only
    // adds a plural-agreement problem in five languages.
    case FIELD_TYPE.days:
    case FIELD_TYPE.hours: {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? formatNumber(numeric, locale) : String(value);
    }

    case FIELD_TYPE.percent: {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? `${formatNumber(numeric, locale)}%` : String(value);
    }

    case FIELD_TYPE.money:
    case FIELD_TYPE.number: {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? formatNumber(numeric, locale) : String(value);
    }

    case FIELD_TYPE.color:
      return String(value).replace(/^FF/i, '#');

    case FIELD_TYPE.array: {
      if (!Array.isArray(value)) return formatUnknownValue(value, translate, locale);
      if (value.every((item) => item === null || typeof item !== 'object')) {
        return value.map((item) => String(item)).join(', ');
      }
      const labels = value.map(getReferenceLabel).filter(Boolean);
      if (labels.length === value.length) return labels.join(', ');
      return translate('value.itemCount', {
        count: value.length,
        defaultValue: `${value.length}`,
      });
    }

    default:
      return formatUnknownValue(value, translate, locale);
  }
};

/**
 * Values older audit rows carry for the allowance methods.
 *
 * Those rows were written from the *transformed* company document, so the
 * stored value is the translation key the interface would have shown
 * (`calculationMethods.perMonth`) rather than the method itself. Newer rows
 * hold the plain key, because the diff now reads the raw document — see the
 * note in `company.services.js`. Both shapes are still in the collection and
 * both have to read.
 *
 * 🚨 The same transform also stringified the `calculate` **functions** onto the
 * record, so a value can be an entire arrow function. Printing it is worse than
 * useless — it fills the panel with code for a change nobody made to code — so
 * it is named for what it is instead.
 */
const CALCULATION_METHOD_PREFIX = 'calculationMethods.';

/**
 * @param {*} value - Stored value.
 * @param {Function} translate - Bound `t`.
 * @returns {string|null} A readable method name, or null when this is not one.
 */
const formatCalculationMethod = (value, translate) => {
  if (typeof value !== 'string' || !value.startsWith(CALCULATION_METHOD_PREFIX)) {
    return null;
  }

  const rest = value.slice(CALCULATION_METHOD_PREFIX.length);

  // Anything holding an arrow, a brace or a parenthesis is the stored function,
  // not a method name.
  if (/[(){}]|=>/.test(rest)) {
    return translate('value.formula', { defaultValue: 'formula' });
  }

  return translate(`enum.allowanceCalculationMethod.${rest}`, {
    defaultValue: rest,
  });
};

/**
 * A stored object written out as the settings it holds.
 *
 * Measured against a year of real change sets: the objects that actually reach
 * the screen are shallow and scalar — the timesheet review rules
 * (`maxHoursPerDay: 12, flagPartialPublicHoliday: true`), a validity span
 * (`validFrom` / `validTo`), a currency (`key`, `label`, `currencyCode`).
 * Naming them "composite value" threw away everything a reader came for, and
 * the change was recorded, shown, and unreadable.
 *
 * Deliberately shallow: anything holding a nested object or a long list of
 * fields is a record, not a value, and falls through to the summary. Keys are
 * translated where the dictionary knows them; where it does not, the raw key
 * still says more than nothing.
 *
 * @param {Object} value - Stored object.
 * @param {Function} translate - Bound `t`.
 * @param {string} locale - For dates inside the object.
 * @returns {string|null} A written-out value, or null when this is not one.
 */
const formatShallowObject = (value, translate, locale) => {
  const entries = Object.entries(value);

  if (!entries.length || entries.length > 8) return null;

  const scalar = entries.every(
    ([, item]) =>
      item === null ||
      item === undefined ||
      typeof item !== 'object' ||
      item instanceof Date
  );
  if (!scalar) return null;

  const parts = entries
    .filter(([, item]) => item !== null && item !== undefined && item !== '')
    .map(([key, item]) => {
      const label = translate(`field.${key}`, { defaultValue: key });

      if (typeof item === 'boolean') {
        return `${label}: ${
          item
            ? translate('value.yes', { defaultValue: 'Da' })
            : translate('value.no', { defaultValue: 'Ne' })
        }`;
      }

      const asDate = parseStoredDate(item);
      // Only strings that are unambiguously dates, so an account number or a
      // tax id is never turned into a day.
      if (
        asDate &&
        (item instanceof Date ||
          (typeof item === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(item)))
      ) {
        return `${label}: ${asDate.locale(locale).format(DATE_FORMATS.DISPLAY_DOTS)}`;
      }

      return `${label}: ${String(item)}`;
    });

  return parts.length ? parts.join(' · ') : null;
};

/**
 * Whether a stored object is a split bank account rather than a record.
 *
 * Deliberately strict about the key set: a value that merely happens to carry a
 * `part1` is not an account, and guessing wrong here would print two unrelated
 * numbers joined by a dash as though they were one.
 *
 * @param {Object} value - Stored value.
 * @returns {boolean}
 */
const isBankAccountParts = (value) => {
  const keys = Object.keys(value);

  return (
    keys.length > 0 &&
    keys.length <= 3 &&
    keys.every((key) => key === 'part1' || key === 'part2' || key === 'part3')
  );
};

/**
 * Last resort for a value whose field is not in the dictionary. Objects show
 * their most name-like property rather than a JSON dump.
 *
 * @param {*} value - Raw value.
 * @param {Function} translate - Bound `t`.
 * @returns {string}
 */
const formatUnknownValue = (value, translate, locale = 'sr') => {
  if (typeof value === 'boolean') {
    return value
      ? translate('value.yes', { defaultValue: 'Da' })
      : translate('value.no', { defaultValue: 'Ne' });
  }

  if (Array.isArray(value)) {
    if (value.every((item) => item === null || typeof item !== 'object')) {
      return value.map((item) => String(item)).join(', ');
    }
    return translate('value.itemCount', {
      count: value.length,
      defaultValue: `${value.length}`,
    });
  }

  if (value instanceof Date) {
    return dayjs(value).format(DATE_FORMATS.DISPLAY_DOTS);
  }

  if (typeof value === 'object') {
    const label = getReferenceLabel(value);
    if (label) return label;

    // A bank account is stored split in three (`160-0000000000-16`): the model
    // takes a string and a setter turns it into `{part1, part2, part3}`, which
    // is the shape the audit trail records. Read back as an unknown object it
    // printed "composite value" — the change was logged, and the drawer said
    // nothing about what changed.
    if (isBankAccountParts(value)) {
      // 🚨 Some stored accounts hold the literal string "undefined" in every
      // part — written by `${part1}-${part2}-${part3}` over an absent value,
      // and measured in the feed as "undefined-undefined-undefined". They mean
      // "no account", and are dropped rather than printed.
      const parts = [value.part1, value.part2, value.part3].filter(
        (part) =>
          part !== undefined &&
          part !== null &&
          part !== '' &&
          part !== 'undefined' &&
          part !== 'null'
      );

      return parts.length ? parts.join('-') : '';
    }

    const written = formatShallowObject(value, translate, locale);
    if (written) return written;

    return translate('value.structured', { defaultValue: '…' });
  }

  return String(value);
};

/**
 * Formats both sides of a change in one call.
 *
 * @param {Object} change - `{ old, new }` as delivered by the history service.
 * @param {Object} descriptor - Result of `describeField`.
 * @param {Object} options - Same options as `formatHistoryValue`.
 * @returns {{from: string, to: string}}
 */
export const formatHistoryChange = (change = {}, descriptor = {}, options = {}) => ({
  from: formatHistoryValue(change.old, descriptor, options),
  to: formatHistoryValue(change.new, descriptor, options),
});
